<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import WidgetCustomizationSection from 'dashboard/components-next/Settings/WidgetCustomizationSection.vue';
import FormInput from 'dashboard/components-next/input/Input.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InboxChannelConfigurationIdentity from '../components/InboxChannelConfigurationIdentity.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['saveStateChange']);

const { t } = useI18n();
const store = useStore();

const channelName = ref('');
const websiteDomains = ref([]);
const isUrlInputFocused = ref(false);
const isUpdating = ref(false);
const savedConfigurationSnapshot = ref(null);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);
const primaryWebsiteDomain = computed(() => websiteDomains.value[0] || '');
const additionalDomains = computed(() => websiteDomains.value.slice(1));

const getConfigurationSnapshot = () => ({
  channelName: (channelName.value || '').trim(),
  websiteDomains: [...websiteDomains.value].sort(),
});

const snapshotConfiguration = () => {
  savedConfigurationSnapshot.value = JSON.stringify(getConfigurationSnapshot());
};

const hasConfigurationChanges = computed(() => {
  if (!savedConfigurationSnapshot.value) return false;
  return (
    JSON.stringify(getConfigurationSnapshot()) !==
    savedConfigurationSnapshot.value
  );
});

const isConfigurationValid = computed(
  () => Boolean(channelName.value?.trim()) && websiteDomains.value.length > 0
);

const canSaveConfiguration = computed(
  () => hasConfigurationChanges.value && isConfigurationValid.value
);

const normalizeUrl = raw => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const isValidUrl = url => {
  try {
    const normalized = normalizeUrl(url);
    if (!/^https?:\/\//i.test(normalized)) return false;
    // eslint-disable-next-line no-new
    new URL(normalized);
    return true;
  } catch {
    return false;
  }
};

const urlToDomain = url => {
  try {
    return new URL(normalizeUrl(url)).hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
};

const normalizeDomainTag = raw => urlToDomain(normalizeUrl(raw));

const parseDomainsFromInbox = inbox => {
  if (!inbox?.id) return [];

  const domains = [];
  const seen = new Set();

  const addDomain = value => {
    const domain = normalizeDomainTag(value);
    if (!domain || seen.has(domain)) return;
    seen.add(domain);
    domains.push(domain);
  };

  if (inbox.website_url) addDomain(inbox.website_url);

  const allowed = inbox.allowed_domains || '';
  allowed
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean)
    .forEach(addDomain);

  return domains;
};

const onWebsiteDomainAdd = ({ value }) => {
  const domain = normalizeDomainTag(value);
  if (!domain || !isValidUrl(normalizeUrl(value))) {
    websiteDomains.value = websiteDomains.value.filter(tag => tag !== value);
    return;
  }
  const withoutDuplicates = websiteDomains.value.filter(
    tag => tag !== value && normalizeDomainTag(tag) !== domain
  );
  websiteDomains.value = [...withoutDuplicates, domain];
};

const emitSaveState = () => {
  emit('saveStateChange', {
    canSave: canSaveConfiguration.value,
    isUpdating: isUpdating.value || uiFlags.value.isUpdating,
  });
};

watch(
  () => props.inbox,
  inbox => {
    if (!inbox?.id) return;
    channelName.value = inbox.name || '';
    websiteDomains.value = parseDomainsFromInbox(inbox);
    snapshotConfiguration();
    emitSaveState();
  },
  { immediate: true }
);

watch([canSaveConfiguration, isUpdating, uiFlags], emitSaveState, {
  deep: true,
});

const saveConfiguration = async () => {
  const name = channelName.value?.trim();
  if (!name) {
    useAlert(t('INBOX_MGMT.ADD.CHANNEL_NAME.ERROR'));
    return;
  }

  if (!websiteDomains.value.length) {
    useAlert(t('INBOX_MGMT.ADD.WEBSITE_URLS.ERROR'));
    return;
  }

  const primaryUrl = normalizeUrl(websiteDomains.value[0]);
  const allowedDomains = sanitizeAllowedDomains(websiteDomains.value.join(','));

  isUpdating.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      name,
      channel: {
        website_url: primaryUrl,
        allowed_domains: allowedDomains,
      },
    });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    snapshotConfiguration();
    emitSaveState();
  } catch (error) {
    useAlert(error?.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};

defineExpose({
  saveConfiguration,
  get canSaveConfiguration() {
    return canSaveConfiguration.value;
  },
  get isUpdating() {
    return isUpdating.value || uiFlags.value.isUpdating;
  },
});
</script>

<template>
  <div class="flex w-full min-w-0 gap-8">
    <div class="flex w-full min-w-0 flex-col gap-6">
      <div
        class="rounded-2xl bg-n-surface-1 px-6 py-5 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <InboxChannelConfigurationIdentity
          :inbox="inbox"
          :show-channel-name="false"
        />
      </div>

      <div
        class="rounded-2xl bg-n-surface-1 px-6 py-5 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <SettingsFieldSection
          stacked
          :label="$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL')"
        >
          <FormInput
            v-model="channelName"
            :placeholder="$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER')"
          />
        </SettingsFieldSection>

        <SettingsFieldSection
          stacked
          :label="$t('INBOX_MGMT.ADD.WEBSITE_URLS.SETTINGS_LABEL')"
        >
          <template #label>
            {{ $t('INBOX_MGMT.ADD.WEBSITE_URLS.SETTINGS_LABEL') }}
            <Icon
              v-tooltip.top="
                $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.DESCRIPTION')
              "
              icon="i-lucide-info"
              class="size-3.5 shrink-0 cursor-help text-n-slate-10"
            />
          </template>
          <div
            class="flex min-h-8 items-center gap-2 rounded-lg bg-n-alpha-white2 px-3.5 py-1 outline outline-1 outline-offset-[-1px] transition-colors"
            :class="
              isUrlInputFocused
                ? 'outline-n-brand'
                : 'outline-n-weak hover:outline-n-slate-6'
            "
          >
            <TagInput
              v-model="websiteDomains"
              class="min-w-0 flex-1"
              allow-create
              :auto-open-dropdown="false"
              :placeholder="
                websiteDomains.length
                  ? ''
                  : $t('INBOX_MGMT.ADD.WEBSITE_URLS.PLACEHOLDER')
              "
              @add="onWebsiteDomainAdd"
              @focus="isUrlInputFocused = true"
              @on-click-outside="isUrlInputFocused = false"
            />
            <Icon
              icon="i-lucide-chevron-down"
              class="size-4 shrink-0 text-n-slate-11"
            />
          </div>
        </SettingsFieldSection>
      </div>
    </div>

    <div
      class="sticky top-4 hidden w-full min-w-0 max-w-lg shrink-0 self-start xl:block"
    >
      <div
        class="flex w-full flex-col gap-5 overflow-hidden rounded-2xl bg-n-surface-1 px-5 py-5 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <div class="flex flex-col gap-1">
          <span
            class="text-xs font-medium uppercase tracking-[0.08em] text-n-slate-10"
          >
            {{ $t('INBOX_MGMT.WEB_WIDGET_CONFIGURATION.EMBED_SCRIPT_LABEL') }}
          </span>
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ channelName || inbox.name }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{
              primaryWebsiteDomain ||
              $t('INBOX_MGMT.ADD.WEBSITE_URLS.PLACEHOLDER')
            }}
          </p>
        </div>

        <div v-if="websiteDomains.length" class="flex flex-wrap gap-2">
          <span
            class="inline-flex items-center rounded-full bg-n-brand/10 px-3 py-1 text-xs font-medium text-n-brand"
          >
            {{ primaryWebsiteDomain }}
          </span>
          <span
            v-for="domain in additionalDomains"
            :key="domain"
            class="inline-flex items-center rounded-full bg-n-alpha-2 px-3 py-1 text-xs font-medium text-n-slate-11"
          >
            {{ domain }}
          </span>
        </div>

        <WidgetCustomizationSection
          :title="$t('INBOX_MGMT.WEB_WIDGET_CONFIGURATION.EMBED_SCRIPT_LABEL')"
          default-open
        >
          <woot-code
            v-if="inbox.web_widget_script"
            compact
            :script="inbox.web_widget_script"
          />
        </WidgetCustomizationSection>
      </div>
    </div>
  </div>
</template>
